<script setup lang="ts">
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    ZhButton,
    ZhSystemLabel,
} from '@zerohuman/ui'
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { aiAccountPromptOpen, closeAiAccountPrompt } from '@/lib/aiAccountGuard'
import { ACCOUNT_STEP_PATH } from '@/lib/onboardingGate'

/**
 * #1796: what a blocked model-starting action opens. Drawn once, in the shell, and opened only by
 * `requireAiAccount` when it blocks an action: it says exactly "Connect your AI account to start", and its one button
 * goes to the connect card, the only way to add a key. Closing it leaves the person where they were, browsing.
 */
const router = useRouter()

const open = computed({
    get: aiAccountPromptOpen,
    set: (next: boolean) => {
        if (!next) {
            closeAiAccountPrompt()
        }
    },
})

async function connect(): Promise<void> {
    closeAiAccountPrompt()
    await router.push(ACCOUNT_STEP_PATH)
}
</script>

<template>
	<Dialog v-model:open="open">
		<DialogContent class="sm:max-w-md">
			<DialogHeader>
				<ZhSystemLabel tone="muted">AI account</ZhSystemLabel>
				<DialogTitle data-testid="ai-account-prompt-message">Connect your AI account to start</DialogTitle>
				<DialogDescription>
					Pages open without one, but nothing can run until your enterprise has an AI account to run on.
				</DialogDescription>
			</DialogHeader>
			<div class="flex justify-end">
				<ZhButton data-testid="ai-account-prompt-connect" @click="connect">Connect your AI account</ZhButton>
			</div>
		</DialogContent>
	</Dialog>
</template>
